import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AntimonyRoastingContact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    facilityLocation: '',
    operationType: '',
    productionCapacity: '',
    oreSource: '',
    processStage: '',
    roastingTemperature: '',
    so2Emissions: '',
    controlDevices: '',
    hazardousPollutants: '',
    currentPermits: '',
    complianceIssues: '',
    timeline: '',
    specificRequirements: '',
    additionalInfo: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/api/contact/antimony-roasting`, formData);
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will review your antimony roasting inquiry and contact you within 1-2 business days.',
        });
        setFormData({
          firstName: '',
          lastName: '',
          company: '',
          email: '',
          phone: '',
          facilityLocation: '',
          operationType: '',
          productionCapacity: '',
          oreSource: '',
          processStage: '',
          roastingTemperature: '',
          so2Emissions: '',
          controlDevices: '',
          hazardousPollutants: '',
          currentPermits: '',
          complianceIssues: '',
          timeline: '',
          specificRequirements: '',
          additionalInfo: '',
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        type: 'error',
        message: 'There was an error submitting your inquiry. Please try again or call us directly at (907) 687-8263.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Antimony Roasting Inquiry</h1>
          <p className="text-gray-600 mb-8">
            Request expert assistance for your antimony roasting facility air quality compliance needs.
          </p>

          {status.message && (
            <div className={`mb-6 p-4 rounded-lg ${
              status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {status.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company/Organization <span className="text-red-500">*</span></label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-red-500">*</span></label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Facility & Project Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility Location</label>
                  <input type="text" name="facilityLocation" value={formData.facilityLocation} onChange={handleChange} placeholder="City, Alaska" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Operation Type</label>
                  <select name="operationType" value={formData.operationType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
                    <option value="">Select operation type</option>
                    <option value="ore_roasting">Antimony Ore Roasting</option>
                    <option value="concentrate_roasting">Concentrate Roasting</option>
                    <option value="oxide_production">Oxide Production</option>
                    <option value="integrated_operations">Integrated Roasting & Processing</option>
                    <option value="pilot_facility">Pilot Facility/Testing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Production Capacity</label>
                  <input type="text" name="productionCapacity" value={formData.productionCapacity} onChange={handleChange} placeholder="e.g., 10,000 tons/year antimony trioxide" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ore Source & Type</label>
                  <input type="text" name="oreSource" value={formData.oreSource} onChange={handleChange} placeholder="e.g., Stibnite concentrate from Alaska mines" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Process Stage</label>
                  <select name="processStage" value={formData.processStage} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
                    <option value="">Select process stage</option>
                    <option value="planning">Planning/Feasibility Study</option>
                    <option value="design">Engineering & Design</option>
                    <option value="construction">Under Construction</option>
                    <option value="commissioning">Commissioning Phase</option>
                    <option value="operational">Currently Operational</option>
                    <option value="modification">Facility Modification</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Roasting Temperature Range</label>
                  <input type="text" name="roastingTemperature" value={formData.roastingTemperature} onChange={handleChange} placeholder="e.g., 500-650°C" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expected SO₂ Emissions</label>
                  <select name="so2Emissions" value={formData.so2Emissions} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
                    <option value="">Select SO₂ emission level</option>
                    <option value="high">High (over 250 TPY) - Major Source</option>
                    <option value="moderate">Moderate (100-250 TPY)</option>
                    <option value="low">Low (under 100 TPY)</option>
                    <option value="unknown">Unknown - Needs Assessment</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Emission Control Devices</label>
                  <textarea name="controlDevices" value={formData.controlDevices} onChange={handleChange} rows="2" placeholder="e.g., Wet scrubber system, baghouse, acid plant for SO₂ recovery" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Hazardous Air Pollutants of Concern</label>
                  <select name="hazardousPollutants" value={formData.hazardousPollutants} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
                    <option value="">Select primary concerns</option>
                    <option value="so2_antimony">SO₂ & Antimony Particulates</option>
                    <option value="antimony_arsenic">Antimony & Arsenic</option>
                    <option value="heavy_metals">Multiple Heavy Metals (Pb, Sb, As)</option>
                    <option value="comprehensive">Comprehensive HAP Management</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Permit Status</label>
                  <select name="currentPermits" value={formData.currentPermits} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
                    <option value="">Select permit status</option>
                    <option value="no_permit">No Permit - New Facility</option>
                    <option value="title_v">Have Title V Permit</option>
                    <option value="synthetic_minor">Synthetic Minor Permit</option>
                    <option value="need_psd">Need PSD/NSR Permit</option>
                    <option value="modification">Permit Modification Needed</option>
                    <option value="renewal">Permit Renewal Required</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Compliance Issues or Concerns</label>
                  <textarea name="complianceIssues" value={formData.complianceIssues} onChange={handleChange} rows="2" placeholder="Describe any current compliance challenges or regulatory concerns" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Timeline</label>
                  <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500">
                    <option value="">Select timeline</option>
                    <option value="immediate">Immediate (within 2 weeks)</option>
                    <option value="1_month">Within 1 month</option>
                    <option value="1-3_months">1-3 months</option>
                    <option value="3-6_months">3-6 months</option>
                    <option value="6-12_months">6-12 months</option>
                    <option value="12+_months">12+ months</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns <span className="text-red-500">*</span></label>
                  <textarea name="specificRequirements" value={formData.specificRequirements} onChange={handleChange} required rows="4" placeholder="e.g., Need SO₂ control system design, NSPS compliance for roasting operations, BACT analysis, PSD air quality modeling, Title V permit application, CEMS requirements" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                  <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows="3" placeholder="Any other details about your project or specific questions..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500"></textarea>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-slate-700 text-white py-4 px-6 rounded-lg font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AntimonyRoastingContact;
